import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { SEOHead } from "@/components/seo/SEOHead";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";

export default function CookiePolicy() {
  return (
    <>
      <SEOHead 
        title="Cookie Policy | Aiveliq360"
        description="Learn about how Aiveliq360 uses cookies and similar tracking technologies. Understand what cookies we use and how to manage your preferences."
        canonicalUrl="https://aiveliq360.com/cookies"
      />
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border py-3">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={aiveliqLogo} alt="Aiveliq360" className="h-6 object-contain" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-1.5">
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Home
              </Button>
            </Link>
          </div>
        </nav>

        <main className="max-w-4xl mx-auto px-4 py-12">
          <h1 className="text-3xl font-bold mb-8">Cookie Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: December 11, 2025</p>

          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4">1. What Are Cookies</h2>
              <p className="text-muted-foreground">
                Cookies are small text files that are stored on your device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">2. How We Use Cookies</h2>
              <p className="text-muted-foreground mb-4">Aiveliq360 uses cookies for the following purposes:</p>
              
              <h3 className="text-lg font-medium mb-2">2.1 Essential Cookies</h3>
              <p className="text-muted-foreground mb-4">
                These cookies are necessary for the website to function properly. They enable core functionality such as security, authentication, and session management.
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border border-border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 border-b border-border">Cookie Name</th>
                      <th className="text-left p-3 border-b border-border">Purpose</th>
                      <th className="text-left p-3 border-b border-border">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="p-3 border-b border-border">sb-auth-token</td>
                      <td className="p-3 border-b border-border">Authentication and session management</td>
                      <td className="p-3 border-b border-border">Session</td>
                    </tr>
                    <tr>
                      <td className="p-3 border-b border-border">theme</td>
                      <td className="p-3 border-b border-border">Stores user theme preference</td>
                      <td className="p-3 border-b border-border">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium mb-2">2.2 Analytics Cookies</h3>
              <p className="text-muted-foreground mb-4">
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border border-border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 border-b border-border">Cookie Name</th>
                      <th className="text-left p-3 border-b border-border">Purpose</th>
                      <th className="text-left p-3 border-b border-border">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="p-3 border-b border-border">_ga</td>
                      <td className="p-3 border-b border-border">Google Analytics - distinguishes users</td>
                      <td className="p-3 border-b border-border">2 years</td>
                    </tr>
                    <tr>
                      <td className="p-3 border-b border-border">_ga_*</td>
                      <td className="p-3 border-b border-border">Google Analytics 4 - persists session state</td>
                      <td className="p-3 border-b border-border">2 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium mb-2">2.3 Functional Cookies</h3>
              <p className="text-muted-foreground">
                These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">3. Third-Party Cookies</h2>
              <p className="text-muted-foreground mb-4">
                Some cookies are placed by third-party services that appear on our pages. We use the following third-party services:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
                <li><strong>Stripe:</strong> For secure payment processing</li>
                <li><strong>Supabase:</strong> For authentication and backend services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">4. Managing Cookies</h2>
              <p className="text-muted-foreground mb-4">
                You can control and manage cookies in several ways:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies through settings</li>
                <li><strong>Opt-Out Links:</strong> For Google Analytics, you can install the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Analytics Opt-out Browser Add-on</a></li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Note: Blocking essential cookies may affect the functionality of our Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">5. Do Not Track</h2>
              <p className="text-muted-foreground">
                Some browsers have a "Do Not Track" feature that signals to websites that you do not want to be tracked. Our website currently does not respond to "Do Not Track" signals.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">6. Updates to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4">7. Contact Us</h2>
              <p className="text-muted-foreground">
                If you have questions about our use of cookies, please contact us at:<br />
                <strong>Email:</strong> privacy@aiveliq360.com
              </p>
            </section>
          </div>
        </main>

        <footer className="border-t border-border py-8">
          <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <img src={aiveliqLogo} alt="Aiveliq360" className="h-6 object-contain" />
            <nav className="flex items-center gap-4 text-xs text-muted-foreground">
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/cookies" className="text-foreground">Cookies</Link>
            </nav>
            <p className="text-xs text-muted-foreground">© 2025 Aiveliq360</p>
          </div>
        </footer>
      </div>
    </>
  );
}
